import { Dexie } from 'dexie'
import moment from 'moment'
import type { Devi_CompletedData } from '../Devi/Devi.CompletedData'
import type { Devi_InactivitySettings } from '../Devi/Devi.inactivity'
import type { Devi_CameraSettings } from '../Devi/Devi.cameraBrowserApi'
import type { Devi_InProgressData } from '../Devi/Devi.InProgressData'
import type { Devi_TodoPageFilters } from '../Devi/Devi.todoPageFilters'
import type { Upload } from '../../../app-core/lib/upload/Upload'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import {
    IDB_uploads as oldUploadsTable,
    IDB_fileChunks as oldFileChunksTable,
    IDB_cachedImages as oldCachedImages,
} from './IDB.dexie'
import type { User_LoginUser } from '../Devi/Devi.recentUsers'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import { Uplo_deleteUploads } from '../Uplo/Uplo.delete'
import type { Devi_TodoPageRecentSearches } from '../Devi/Devi.todoPageRecentSearches'
import type { Devi_ProcedureTimelineFilters } from '../Devi/Devi.procedureTimelineFilters'

/**
 * Separate database for data that persists through logouts
 */
export let IDB_deviceDB: Dexie

export function IDB_deviceDBInitialise() {
    const logger = Logg_createLogger('deviceDBInitialise')

    IDB_deviceDB = new Dexie('DeviceDB')

    // http://dexie.org/docs/Version/Version.stores()#description

    IDB_deviceDB.version(1)
        .stores({
            deviceLookup: '',
            deviceInProgressData: '++id, [customerId+userId]',
        })
        .upgrade(async (_) => {
            // Migrate the current inactivity settings from local storage
            //

            const str = localStorage.getItem('inactivitySettings')
            localStorage.removeItem('inactivitySettings')

            if (str == null || str === 'undefined') return

            const settings = JSON.parse(str)
            await IDB_inactivitySettings().set(settings)
        })

    IDB_deviceDB.version(2)
        .stores({
            uploads: ',groupId',
            fileChunks: '',
            deviceCompletedData: '++id',
        })
        .upgrade((tx) =>
            Promise.all([
                oldUploadsTable().toArray().then(tx.table('uploads').bulkAdd),
                // File chunks can be quite large, not a great idea to have it
                // all in memory
                oldFileChunksTable().each(tx.table('fileChunks').add),
            ])
        )

    IDB_deviceDB.version(3).stores({
        recentUsers: '&id',
    })

    IDB_deviceDB.version(4).upgrade(async () => {
        const oneWeekAgo = +moment().subtract(1, 'weeks')

        const uploads = await IDB_uploads()
            .filter((u) => +u.fileUploadData.createdAt < oneWeekAgo)
            .toArray()

        await Uplo_deleteUploads(logger, uploads)
    })

    IDB_deviceDB.version(5).upgrade(async () => {
        await IDB_deviceInProgressData()
            .toCollection()
            .modify((row) => {
                if (row.type === 'flowReport') {
                    row.value.uploadState = 'pendingUpload'
                } else if (row.type === 'flowAnswer') {
                    row.value.uploadState = 'pendingUpload'
                }
            })
    })

    IDB_deviceDB.version(6)
        .stores({ cachedImages: '' })
        .upgrade(async () => {
            const urls = await oldCachedImages().toCollection().primaryKeys()
            for (const url of urls) {
                const image = await oldCachedImages().get(url)
                await IDB_cachedImages().put(image, url)
            }
        })
}

// Wrappers around dexieDB table in order to provide types
export const IDB_cachedImages = () => IDB_deviceDB.table<{ data: Blob }, string>('cachedImages')
export const IDB_deviceCompletedData = () => IDB_deviceDB.table<Devi_CompletedData, number>('deviceCompletedData')
export const IDB_deviceInProgressData = () => IDB_deviceDB.table<Devi_InProgressData, number>('deviceInProgressData')
export const IDB_deviceRecentUsers = () => IDB_deviceDB.table<User_LoginUser, number>('recentUsers')
export const IDB_fileChunks = () => IDB_deviceDB.table<{ data: ArrayBuffer }, string>('fileChunks')
export const IDB_uploads = () => IDB_deviceDB.table<Upload, FileId>('uploads')

// Create single key lookups
export const IDB_cameraBrowserApi = () => createDeviceLookup<Devi_CameraSettings>('cameraBrowserApi')
export const IDB_inactivitySettings = () => createDeviceLookup<Devi_InactivitySettings>('inactivitySettings')
export const IDB_todoPageFilters = () => createDeviceLookup<Devi_TodoPageFilters>('todoPageFilters')
export const IDB_todoPageRecentSearches = () =>
    createDeviceLookup<Devi_TodoPageRecentSearches>('todoPageRecentSearches')

export const IDB_procedureTimelineFilters = () =>
    createDeviceLookup<Devi_ProcedureTimelineFilters>('procedureTimelineFilters')

function createDeviceLookup<T>(key: string) {
    return {
        get: (): Promise<T> => IDB_deviceDB.table('deviceLookup').get(key),
        set: (value: T) => IDB_deviceDB.table('deviceLookup').put(value, key),
    }
}

export function clearUploads() {
    return Promise.all([IDB_uploads().clear(), IDB_fileChunks().clear()])
}
